/**
 * Markdown projection kept current from the Y.Doc for `documents.textV2` and search (AC 14.4).
 * Minimal, dependency-free serializer covering the corpus block/inline set — ported in spirit
 * from `knowit-main/shared/editor`, kept internal so v2 never imports a v1 path.
 */

import type { Node as PMNode } from 'prosemirror-model';

/** Serialize a document node to markdown text. Live-object nodes emit their searchable summary. */
export function toTextV2(doc: PMNode): string {
  const out: string[] = [];
  doc.forEach((block) => out.push(serializeBlock(block)));
  return out.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
}

function serializeBlock(node: PMNode): string {
  switch (node.type.name) {
    case 'heading':
      return `${'#'.repeat(node.attrs.level as number)} ${node.textContent}`;
    case 'blockquote':
      return node.textContent
        .split('\n')
        .map((l) => `> ${l}`)
        .join('\n');
    case 'code_block':
      return `\`\`\`${node.attrs.language ?? ''}\n${node.textContent}\n\`\`\``;
    case 'hr':
      return '---';
    case 'bullet_list':
      return listItems(node).map((t) => `- ${t}`).join('\n');
    case 'ordered_list':
      return listItems(node).map((t, i) => `${i + 1}. ${t}`).join('\n');
    case 'reminder_chip':
    case 'task_card':
    case 'decision_card':
    case 'truth_card':
      return String(node.attrs.summary ?? '');
    case 'widget':
    case 'chart':
      // Atom nodes have empty textContent; alt is the searchable/readable face of a figure.
      return String(node.attrs.alt ?? '');
    default:
      return node.textContent;
  }
}

function listItems(list: PMNode): string[] {
  const items: string[] = [];
  list.forEach((li) => items.push(li.textContent));
  return items;
}
